import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Pessoas from './pages/Pessoas';
import Lares from './pages/Lares';
import Diarios from './pages/Diarios';
import DiariosAcessos from './pages/DiariosAcessos';
import Atividades from './pages/Atividades';
import DiaryList from './pages/Diary';
import DiaryForm from './pages/Diary/DiaryForm';
import DiaryView from './pages/Diary/DiaryView';
import NotFound from './pages/NotFound';

// Components
import MainLayout from './components/Layout/MainLayout';
import Loading from './components/Loading';

// Contexts
import { AuthProvider, useAuth } from './contexts/auth';
import api from './services/api';

// React Router Future Flags Configuration
const routerConfig = {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }
};

// Auth Guard
const PrivateRoute = ({ children }) => {
  const { signed, loading, tokenValidated, validateToken, authError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [validationInProgress, setValidationInProgress] = useState(false);
  
  useEffect(() => {
    const checkAuthentication = async () => {
      console.log('PrivateRoute: Verificando autenticação');
      console.log('Estado de autenticação detalhado:', { 
        signed, 
        loading, 
        tokenValidated, 
        validationInProgress,
        authError,
        pathname: location.pathname
      });
      
      // Se a autenticação ainda está carregando, aguardamos
      if (loading) {
        console.log('PrivateRoute: Autenticação ainda carregando...');
        return;
      }
      
      // Se o usuário não está autenticado, tentamos validar o token
      if (!signed) {
        console.log('PrivateRoute: Usuário não autenticado');
        
        // Verificar se o token precisa ser validado e se não estamos em processo de validação
        if (!tokenValidated && !validationInProgress) {
          console.log('PrivateRoute: Token não validado, iniciando validação...');
          
          // Marcar que estamos validando para evitar múltiplas tentativas
          setValidationInProgress(true);
          
          try {
            console.log('PrivateRoute: Chamando validateToken()');
            const isValid = await validateToken();
            
            // Resetar o estado de validação em andamento
            setValidationInProgress(false);
            
            if (!isValid) {
              console.log('PrivateRoute: Token inválido após validação, redirecionando para login');
              navigate('/login', { 
                state: { 
                  from: location.pathname,
                  authError: authError || 'Sessão expirada. Por favor, faça login novamente.'
                },
                replace: true // Usar replace para evitar problemas com o histórico
              });
            } else {
              console.log('PrivateRoute: Token validado com sucesso');
            }
          } catch (error) {
            console.error('PrivateRoute: Erro ao validar token:', error);
            
            // Resetar o estado de validação em andamento mesmo em caso de erro
            setValidationInProgress(false);
            
            navigate('/login', { 
              state: { 
                from: location.pathname,
                authError: 'Erro ao validar sessão. Por favor, faça login novamente.'
              },
              replace: true // Usar replace para evitar problemas com o histórico
            });
          }
        } else if (!tokenValidated && validationInProgress) {
          // Se estamos validando, apenas aguardar
          console.log('PrivateRoute: Validação em andamento, aguardando...');
        } else if (!tokenValidated) {
          // Se o token não é válido e não estamos validando, redirecionar
          console.log('PrivateRoute: Token inválido, redirecionando para login');
          navigate('/login', { 
            state: { 
              from: location.pathname,
              authError: authError || 'Sessão expirada. Por favor, faça login novamente.'
            },
            replace: true // Usar replace para evitar problemas com o histórico
          });
        }
      } else {
        console.log('PrivateRoute: Usuário autenticado, permanecendo na rota protegida');
        // Resetar o estado de validação quando o usuário está autenticado
        if (validationInProgress) {
          setValidationInProgress(false);
        }
      }
    };
    
    checkAuthentication();
  }, [signed, loading, tokenValidated, navigate, location.pathname, validateToken, validationInProgress, authError]);
  
  // Exibir componente de carregamento enquanto verificamos a autenticação
  if (loading || (!signed && validationInProgress)) {
    console.log('PrivateRoute: Exibindo componente de carregamento');
    return <Loading />;
  }
  
  // Se o usuário está autenticado, renderizamos o conteúdo
  if (signed) {
    console.log('PrivateRoute: Usuário autenticado, renderizando conteúdo protegido');
    return children;
  }
  
  // Se chegamos aqui, o usuário não está autenticado e não estamos validando
  console.log('PrivateRoute: Usuário não autenticado, aguardando redirecionamento');
  return <Loading />;
};

const App = () => {
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <SnackbarProvider maxSnack={3}>
          <CssBaseline />
          <Router {...routerConfig}>
            <Routes>
              <Route path="/login" element={<Login />} />
              
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <MainLayout />
                  </PrivateRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="pessoas" element={<Pessoas />} />
                <Route path="lares" element={<Lares />} />
                <Route path="diary" element={<DiaryList />} />
                <Route path="diary/new" element={<DiaryForm />} />
                <Route path="diary/:id" element={<DiaryView />} />
                <Route path="diary/:id/edit" element={<DiaryForm />} />
                <Route path="diarios" element={<Diarios />} />
                <Route path="diarios-acessos" element={<DiariosAcessos />} />
                <Route path="atividades" element={<Atividades />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Router>
        </SnackbarProvider>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
